import fp from 'lodash/fp';
import React, { CSSProperties, PropsWithChildren, useRef } from 'react';
import { VariableSizeList, VariableSizeListProps, shouldComponentUpdate } from 'react-window';

import { Text, cn, useRemUpdatedCallback, multipleRef, useMemoDeps } from 'Modules/ui';

import { paginationStatuses } from '../../state/pagination/statuses';
import { getDomainTypeFromPath } from '../../state/pagination/utils';
import { PaginationType, StorageType } from '../../state/types';
import { usePagination, useStorage } from '../../utils';
import { PaginationLoader } from '../PaginationLoader';
import { PaginationListProps } from './types';
import styles from './index.module.scss';


const ListProxy = React.forwardRef<VariableSizeList, VariableSizeListProps>(
    (props, forwardedRef) => {
        const listRef = useRef<VariableSizeList>();
        useRemUpdatedCallback(() => {
            if (listRef.current)
                listRef.current.resetAfterIndex(0);
        });
        return <VariableSizeList ref={multipleRef(listRef, forwardedRef)} {...props} />;
    },
);

const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isGuid = (elem: string) => guidPattern.test(elem);

const isIdOrGuid = (elem: number | string) => fp.isNumber(elem) || isGuid(elem);

export const firstElementLoaded = fp.pipe(fp.get('[0]'), isIdOrGuid);

const getFirstUpdatedIndex = fp.pipe(
    fp.minBy(fp.pipe(fp.split(':'), fp.get(0))),
    fp.split(':'),
    fp.get(0),
    fp.toNumber,
);


type PaginationListClassProps<TItem> = ConnectTypes & PaginationListProps<TItem>;

type ItemProps<TItem> = PaginationListClassProps<TItem>
& { index: number, style: CSSProperties, onRowSelect: (element: React.ReactText) => void };


class Item<TItem> extends React.Component<ItemProps<TItem>> {
    shouldComponentUpdate = shouldComponentUpdate.bind(this);

    render() {
        const {
            selectedId,
            onRowSelected,
            storage,
            paginationElements,
            paginationQueue,
            domain,
            elementRenderer: ElementRenderer,
            refreshOnDirty,

            index,
            style,
            onRowSelect,
        } = this.props;
        const element = paginationElements[index];
        const queueStatus =
            paginationQueue[element] || storage[element] && paginationStatuses.COMPLETE;
        const isSelected =
            selectedId &&
            element &&
            storage[element] &&
            (storage[element].Uid || storage[element].Id) === selectedId;

        return (
            <ElementRenderer
                refreshOnDirty={refreshOnDirty}
                index={index}
                style={style}
                queueStatus={queueStatus}
                domain={domain}
                data={storage[element]}
                isSelected={isSelected}
                onClick={
                    onRowSelected && element !== selectedId
                        ? () => onRowSelect(element)
                        : undefined
                }
            />
        );
    }
}


class PaginationListClass<TItem> extends React.PureComponent<PaginationListClassProps<TItem>> {
    virtualizedList = React.createRef<VariableSizeList>();

    componentDidMount() {
        const { paginationElements, needFirstRowSelect } = this.props;
        if (needFirstRowSelect && firstElementLoaded(paginationElements))
            this.onRowSelect(paginationElements[0]);
    }

    componentDidUpdate(prevProps: PaginationListClassProps<TItem>) {
        const { path, paginationElements, rowHeight, selectedId, needFirstRowSelect } = this.props;

        if (selectedId && !fp.includes(selectedId, paginationElements))
            this.onRowSelect(null);

        if (!selectedId || prevProps.path !== path) {
            const shouldSelectFirstElement =
                needFirstRowSelect &&
                (prevProps.path !== path || !firstElementLoaded(prevProps.paginationElements));

            if (shouldSelectFirstElement && firstElementLoaded(paginationElements))
                this.onRowSelect(paginationElements[0]);
        }

        if (fp.isFunction(rowHeight) && prevProps.path === path)
            this.recalculateRowHeights(prevProps.paginationQueue);
    }

    onRowSelect = (element: React.ReactText) => {
        const { storage, domain, onRowSelected } = this.props;
        return onRowSelected(element ? storage[element] : null, domain);
    };

    recalculateRowHeights = (prevPaginationQueue: PaginationType['queue']) => {
        const { paginationQueue } = this.props;

        const updatedQueue = fp.map(
            '0',
            fp.differenceBy(
                fp.get(1),
                fp.toPairs(prevPaginationQueue),
                fp.toPairs(paginationQueue),
            ),
        );

        if (updatedQueue.length > 0) {
            const resetStart = getFirstUpdatedIndex(updatedQueue);
            if (this.virtualizedList.current)
                this.virtualizedList.current.resetAfterIndex(resetStart, false);
        }
    };

    rowHeightAsFunction = (index: number) => {
        const { rowHeight, storage, paginationElements } = this.props;
        // eslint-disable-next-line no-extra-parens
        return (rowHeight as Function)(storage[paginationElements[index]]);
    };

    renderPlaceholder = () => (
        <Text textType='info' textCompact component='div' className={styles.placeholder}>
            {this.props.placeholderText}
        </Text>
    );

    render() {
        const {
            path,
            className,
            refreshOnDirty,
            rowHeight,
            selectedId,
            containerRef,
            placeholderRenderer,
            itemData,
        } = this.props;
        return (
            <div className={cn(styles.container, className)}>
                <PaginationLoader<VariableSizeListProps>
                    path={path}
                    container={ListProxy}
                    // @ts-ignore
                    containerRef={multipleRef(this.virtualizedList, containerRef)}
                    placeholderRenderer={placeholderRenderer || this.renderPlaceholder}
                    refreshOnDirty={refreshOnDirty}
                    className={styles.list}
                    itemData={itemData || selectedId}
                    itemSize={
                        fp.isFunction(rowHeight) ? this.rowHeightAsFunction : fp.constant(rowHeight)
                    }
                >
                    {({ index, style }) => (
                        <Item
                            {...this.props}
                            index={index}
                            style={style}
                            onRowSelect={this.onRowSelect}
                        />
                    )}
                </PaginationLoader>
            </div>
        );
    }
}

type ConnectTypes = {
    paginationElements: PaginationType['elements'];
    paginationQueue: PaginationType['queue'];
    storage: StorageType<any>;
    domain: string;
};

export function PaginationList<TItem>({
    path,
    ...props
}: PropsWithChildren<PaginationListProps<TItem>>) {
    const paginationElements = usePagination(path, 'elements');
    const paginationQueue = usePagination(path, 'queue');
    const domain = useMemoDeps(getDomainTypeFromPath, [path]);
    const storage = useStorage(domain);
    return (
        <PaginationListClass
            path={path}
            paginationElements={paginationElements}
            paginationQueue={paginationQueue}
            domain={domain}
            storage={storage}
            {...props}
        />
    );
}

PaginationList.defaultProps = {
    needFirstRowSelect: true,
};

export * from './types';
